import type {SocialPost} from '@Components/social-media/post.vo';
import {wasWizardOpenedFromFakeDoorPost} from '@Libraries/marketing-planner.library';
import {CACHE_KEY_FOR_SCHEDULING} from '@Libraries/social-media-grid.library';

const NOON_HOUR = 12;
const ONE_HOUR_OFFSET = 1;
const MIN_SCHEDULING_TIME_GAP_MINS = 15;
const ONE_HR_MINS = 60;

export const deletePrefilledSchedulingDate = (): void => {
  window.PMW.deleteCacheItem(CACHE_KEY_FOR_SCHEDULING);
};

export const isSchedulingDateSameAsCurrentDate = (date: Date): boolean => {
  const currentDate = new Date();
  return currentDate.getFullYear() === date.getFullYear() && currentDate.getMonth() === date.getMonth() && currentDate.getDate() === date.getDate();
};

export const getSchedulingDateAtNoon = (date: Date): Date => {
  const result = new Date(date.getTime());
  result.setHours(NOON_HOUR, 0, 0, 0);
  return result;
};

export const getSchedulingDateAtNextHour = (date: Date, now: Date = new Date()): Date => {
  const result = new Date(date.getTime());
  const currentMinutes = now.getMinutes();
  const currentHours = now.getHours();

  const minutesToNextHour = ONE_HR_MINS - currentMinutes;
  const isTimeToNextHrLessThanMinSchedulingGap = minutesToNextHour < MIN_SCHEDULING_TIME_GAP_MINS;

  if (isTimeToNextHrLessThanMinSchedulingGap) {
    result.setHours(currentHours, currentMinutes + MIN_SCHEDULING_TIME_GAP_MINS, 0, 0);
  } else {
    result.setHours(currentHours + ONE_HOUR_OFFSET, 0, 0, 0);
  }

  return result;
};

/**
 * Returns the post's timeZoneOffset when it is a defined non-null number,
 * or null otherwise (including when post itself is undefined).
 */
export const getSanitizedTimeZoneOffset = (post: SocialPost | undefined): number | null => {
  if (post?.timeZoneOffset !== null && post?.timeZoneOffset !== undefined) {
    return post.timeZoneOffset;
  }
  return null;
};

export const getDateObjectForPrefilledDate = (prefilledDate: string): Date => {
  const schedulingDate = new Date(prefilledDate);

  if (isSchedulingDateSameAsCurrentDate(schedulingDate)) {
    return getSchedulingDateAtNextHour(schedulingDate);
  }

  return getSchedulingDateAtNoon(schedulingDate);
};

/**
 * A fake-door post card caches the exact ideal repost time, which must prefill unchanged.
 * Content-planner day-cell writes carry a placeholder time, so they normalize to noon on
 * future days or to the next hour today.
 */
export const getSchedulingDateForPrefill = (prefilledDate: string): Date => {
  if (wasWizardOpenedFromFakeDoorPost()) {
    return new Date(prefilledDate);
  }

  return getDateObjectForPrefilledDate(prefilledDate);
};
